/**
 * Client Outcome Tracking Service
 * Tracks and analyzes client health outcomes over time
 */

import { supabase } from './supabase'

/**
 * Record client outcome
 */
export const recordOutcome = async (organizationId, clientId, outcomeData) => {
  try {
    const { data, error } = await supabase
      .from('client_outcomes')
      .insert({
        organization_id: organizationId,
        client_id: clientId,
        outcome_type: outcomeData.type, // 'symptom_improvement', 'goal_achievement', 'metric_change', 'quality_of_life'
        outcome_value: outcomeData.value,
        outcome_unit: outcomeData.unit,
        baseline_value: outcomeData.baseline,
        measurement_date: outcomeData.date || new Date().toISOString(),
        notes: outcomeData.notes,
        recorded_by: outcomeData.recordedBy,
      })
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error recording outcome:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get client outcomes
 */
export const getClientOutcomes = async (organizationId, clientId, filters = {}) => {
  try {
    let query = supabase
      .from('client_outcomes')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('client_id', clientId)
      .order('measurement_date', { ascending: false })

    if (filters.outcomeType) {
      query = query.eq('outcome_type', filters.outcomeType)
    }

    if (filters.startDate) {
      query = query.gte('measurement_date', filters.startDate)
    }

    if (filters.endDate) {
      query = query.lte('measurement_date', filters.endDate)
    }

    const { data, error } = await query

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting client outcomes:', error)
    return { success: false, error: error.message, data: [] }
  }
}

/**
 * Get outcome analytics for a client
 */
export const getClientOutcomeAnalytics = async (organizationId, clientId) => {
  try {
    const { data: outcomes, error } = await supabase
      .from('client_outcomes')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('client_id', clientId)
      .order('measurement_date', { ascending: true })

    if (error) throw error

    if (!outcomes || outcomes.length === 0) {
      return {
        success: true,
        data: {
          totalOutcomes: 0,
          outcomeTypes: {},
          trends: {},
          improvementRate: 0,
        },
      }
    }

    // Calculate analytics
    const outcomeTypes = {}
    const trends = {}

    outcomes.forEach((outcome) => {
      // Count by type
      outcomeTypes[outcome.outcome_type] = (outcomeTypes[outcome.outcome_type] || 0) + 1

      // Track trends
      if (!trends[outcome.outcome_type]) {
        trends[outcome.outcome_type] = []
      }
      trends[outcome.outcome_type].push({
        date: outcome.measurement_date,
        value: outcome.outcome_value,
        baseline: outcome.baseline_value,
      })
    })

    // Calculate improvement rate
    let improvements = 0
    outcomes.forEach((outcome) => {
      if (outcome.baseline_value && outcome.outcome_value) {
        if (outcome.outcome_type === 'symptom_improvement' || outcome.outcome_type === 'quality_of_life') {
          // Higher is better
          if (outcome.outcome_value > outcome.baseline_value) {
            improvements++
          }
        } else if (outcome.outcome_type === 'metric_change') {
          // Depends on metric type - assume lower is better for now
          if (outcome.outcome_value < outcome.baseline_value) {
            improvements++
          }
        }
      }
    })

    const improvementRate = outcomes.length > 0 ? (improvements / outcomes.length) * 100 : 0

    return {
      success: true,
      data: {
        totalOutcomes: outcomes.length,
        outcomeTypes,
        trends,
        improvementRate: Math.round(improvementRate),
        latestOutcome: outcomes[outcomes.length - 1],
        firstOutcome: outcomes[0],
      },
    }
  } catch (error) {
    console.error('Error getting outcome analytics:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get organization-wide outcome statistics
 */
export const getOrganizationOutcomeStats = async (organizationId, timeRange = 30) => {
  try {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - timeRange)

    const { data: outcomes, error } = await supabase
      .from('client_outcomes')
      .select('*')
      .eq('organization_id', organizationId)
      .gte('measurement_date', startDate.toISOString())

    if (error) throw error

    const stats = {
      totalOutcomes: outcomes?.length || 0,
      uniqueClients: new Set(outcomes?.map((o) => o.client_id) || []).size,
      outcomeTypes: {},
      averageImprovementRate: 0,
    }

    if (outcomes && outcomes.length > 0) {
      // Count by type
      outcomes.forEach((outcome) => {
        stats.outcomeTypes[outcome.outcome_type] = (stats.outcomeTypes[outcome.outcome_type] || 0) + 1
      })

      // Calculate average improvement rate
      let totalImprovements = 0
      let totalComparisons = 0

      outcomes.forEach((outcome) => {
        if (outcome.baseline_value && outcome.outcome_value) {
          totalComparisons++
          if (outcome.outcome_value > outcome.baseline_value) {
            totalImprovements++
          }
        }
      })

      stats.averageImprovementRate =
        totalComparisons > 0 ? Math.round((totalImprovements / totalComparisons) * 100) : 0
    }

    return { success: true, data: stats }
  } catch (error) {
    console.error('Error getting organization outcome stats:', error)
    return { success: false, error: error.message }
  }
}

export default {
  recordOutcome,
  getClientOutcomes,
  getClientOutcomeAnalytics,
  getOrganizationOutcomeStats,
}

